import React, { useEffect, useState } from "react";
import {
    Breadcrumb,
    Row,
    Col,
    Card,
    notification,
    Button,
    Spin,
    Table,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Switch,
    Popconfirm,
} from "antd";
import { PlusOutlined, HistoryOutlined, FileTextOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import http from "../../helpers/http";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/";
const base_url = `${API_URL}admin/investor`;

function InvestorList() {
    const [investors, setInvestors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [resetRecord, setResetRecord] = useState(null);
    const [resetLoading, setResetLoading] = useState(false);
    const [form] = Form.useForm();
    const [resetForm] = Form.useForm();

    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [globalHistoryModalOpen, setGlobalHistoryModalOpen] = useState(false);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedInvestor, setSelectedInvestor] = useState(null);
    const [historyPagination, setHistoryPagination] = useState({ current: 1, pageSize: 50, total: 0 });

    const fetchInvestors = async () => {
        setLoading(true);
        try {
            const res = await http.get(`${base_url}?t=${Date.now()}`);
            let list = [];
            if (res && Array.isArray(res.data)) {
                list = res.data;
            } else if (res && res.data && Array.isArray(res.data.investors)) {
                list = res.data.investors;
            } else if (Array.isArray(res)) {
                list = res;
            } else if (res && typeof res === "object" && res.investors) {
                list = res.investors;
            }
            setInvestors(list);
        } catch (error) {
            notification.error({
                message: "Error fetching investors",
                description: error?.message,
            });
            setInvestors([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            const values = await form.validateFields();
            setCreateLoading(true);
            const res = await http.post(base_url, {
                name: values.name?.trim(),
                email: values.email?.trim().toLowerCase(),
                password: values.password,
            });
            if (res != null) {
                notification.success({ message: "Investor created successfully" });
                form.resetFields();
                setCreateModalOpen(false);
                fetchInvestors();
            }
        } catch (error) {
            if (error.errorFields) return;
            notification.error({
                message: "Failed to create investor",
                description: error?.message,
            });
        } finally {
            setCreateLoading(false);
        }
    };

    const handleStatusChange = async (record, checked) => {
        const newStatus = checked ? "active" : "inactive";
        try {
            const res = await http.patch(`${base_url}/${record._id}/status`, { status: newStatus });
            if (res != null) {
                notification.success({ message: `Investor ${newStatus}` });
                fetchInvestors();
            }
        } catch (error) {
            notification.error({
                message: "Failed to update status",
                description: error?.message,
            });
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await http.delete(`${base_url}/${id}`);
            if (res != null) {
                notification.success({ message: "Investor deleted successfully" });
                fetchInvestors();
            }
        } catch (error) {
            notification.error({
                message: "Failed to delete investor",
                description: error?.message,
            });
        }
    };

    const handleResetPassword = async () => {
        try {
            const values = await resetForm.validateFields();
            setResetLoading(true);
            const res = await http.post(`${API_URL}auth/reset-user-password/${resetRecord._id}`, {
                newPassword: values.password,
            });
            if (res != null) {
                notification.success({ message: "Password reset successfully" });
                resetForm.resetFields();
                setResetModalOpen(false);
                setResetRecord(null);
            }
        } catch (error) {
            if (error.errorFields) return;
            notification.error({
                message: "Failed to reset password",
                description: error?.message,
            });
        } finally {
            setResetLoading(false);
        }
    };

    const fetchInvestorHistory = async (investorId, page = 1) => {
        setHistoryLoading(true);
        try {
            const res = await http.get(`${base_url}/${investorId}/logs?page=${page}&limit=50&t=${Date.now()}`);
            const data = res?.data || res;
            setHistoryLogs(data?.logs || []);
            setHistoryPagination({
                current: data?.pagination?.page || 1,
                pageSize: data?.pagination?.limit || 50,
                total: data?.pagination?.total || 0,
            });
        } catch (error) {
            notification.error({
                message: "Error fetching history",
                description: error?.message,
            });
            setHistoryLogs([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const fetchGlobalHistory = async (page = 1) => {
        setHistoryLoading(true);
        try {
            const res = await http.get(`${base_url}/logs?page=${page}&limit=50&t=${Date.now()}`);
            const data = res?.data || res;
            setHistoryLogs(data?.logs || []);
            setHistoryPagination({
                current: data?.pagination?.page || 1,
                pageSize: data?.pagination?.limit || 50,
                total: data?.pagination?.total || 0,
            });
        } catch (error) {
            notification.error({
                message: "Error fetching global history",
                description: error?.message,
            });
            setHistoryLogs([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleViewHistory = (record) => {
        setSelectedInvestor(record);
        setHistoryLogs([]);
        setHistoryModalOpen(true);
        fetchInvestorHistory(record._id, 1);
    };

    const handleViewGlobalHistory = () => {
        setHistoryLogs([]);
        setGlobalHistoryModalOpen(true);
        fetchGlobalHistory(1);
    };

    useEffect(() => {
        fetchInvestors();
    }, []);

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (name) => name || "—",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (email) => email || "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status, record) => {
                const active = status === "active";
                return (
                    <Space>
                        <Switch
                            checked={active}
                            onChange={(checked) => handleStatusChange(record, checked)}
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                        />
                        <Tag color={active ? "green" : "red"}>
                            {status === "active" ? "Active" : "Inactive"}
                        </Tag>
                    </Space>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => {
                            setResetRecord(record);
                            setResetModalOpen(true);
                        }}
                    >
                        Reset Password
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this investor?"
                        description="This will permanently delete the investor and all associated access logs."
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }} />
                    </Popconfirm>
                    <Button
                        type="default"
                        icon={<HistoryOutlined />}
                        onClick={() => handleViewHistory(record)}
                    >
                        History
                    </Button>
                </Space>
            ),
        },
    ];

    const historyColumns = [
        ...(globalHistoryModalOpen ? [{
            title: "User",
            key: "user",
            render: (_, record) => record?.user?.name || record?.user?.email || "—"
        }] : []),
        {
            title: "Login At",
            dataIndex: "loginAt",
            key: "loginAt",
            render: (val) => val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "—"
        },
        {
            title: "Spreadsheet Action",
            key: "spreadsheetAction",
            render: (_, record) => {
                const type = record.accessType || "Auto Redirected";
                const timeStr = record.lastAccessAt
                    ? moment(record.lastAccessAt).format("YYYY-MM-DD HH:mm:ss")
                    : (record.loginAt ? moment(record.loginAt).format("YYYY-MM-DD HH:mm:ss") : "—");
                return `${type} (${timeStr})`;
            }
        },
        {
            title: "Logout At",
            dataIndex: "logoutAt",
            key: "logoutAt",
            render: (val) => val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "Active"
        },
        {
            title: "Duration (Mins)",
            dataIndex: "durationMinutes",
            key: "durationMinutes",
            render: (val) => val !== undefined && val !== null ? parseFloat(val).toFixed(2) : "—"
        }
    ];

    return (
        <div>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>JagKris</Breadcrumb.Item>
                <Breadcrumb.Item>Investors</Breadcrumb.Item>
            </Breadcrumb>

            <Row>
                <Col span={24}>
                    <Card
                        title="Investors"
                        extra={
                            <Space>
                                <Button
                                    icon={<FileTextOutlined />}
                                    onClick={handleViewGlobalHistory}
                                >
                                    Global Activity
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setCreateModalOpen(true)}
                                >
                                    Add Investor
                                </Button>
                            </Space>
                        }
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: 50 }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={investors}
                                rowKey="_id"
                                pagination={{ pageSize: 10 }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                title="Add Investor"
                open={createModalOpen}
                onOk={handleCreate}
                onCancel={() => {
                    form.resetFields();
                    setCreateModalOpen(false);
                }}
                confirmLoading={createLoading}
                okText="Create"
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: "Please enter name" }]}
                    >
                        <Input placeholder="Full name" />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: "Please enter email" },
                            { type: "email", message: "Please enter a valid email" },
                        ]}
                    >
                        <Input type="email" placeholder="investor@example.com" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                            { required: true, message: "Please enter password" },
                            { min: 6, message: "Password must be at least 6 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Minimum 6 characters" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Reset Password for ${resetRecord?.name || "Investor"}`}
                open={resetModalOpen}
                onOk={handleResetPassword}
                onCancel={() => {
                    resetForm.resetFields();
                    setResetModalOpen(false);
                    setResetRecord(null);
                }}
                confirmLoading={resetLoading}
                okText="Reset"
                destroyOnClose
            >
                <Form resetForm={resetForm} form={resetForm} layout="vertical" style={{ marginTop: 16 }}>
                    <p>Enter a new password for <b>{resetRecord?.email}</b></p>
                    <Form.Item
                        name="password"
                        label="New Password"
                        rules={[
                            { required: true, message: "Please enter new password" },
                            { min: 6, message: "Password must be at least 6 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Minimum 6 characters" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal for Specific Investor History */}
            <Modal
                title={`Login History: ${selectedInvestor?.name || "Investor"}`}
                open={historyModalOpen}
                onCancel={() => setHistoryModalOpen(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Table
                    columns={historyColumns}
                    dataSource={historyLogs}
                    rowKey="_id"
                    loading={historyLoading}
                    pagination={{
                        current: historyPagination.current,
                        pageSize: historyPagination.pageSize,
                        total: historyPagination.total,
                        onChange: (page) => fetchInvestorHistory(selectedInvestor?._id, page)
                    }}
                />
            </Modal>

            {/* Modal for Global Activity History */}
            <Modal
                title="Global Activity Logs"
                open={globalHistoryModalOpen}
                onCancel={() => setGlobalHistoryModalOpen(false)}
                footer={null}
                width={900}
                destroyOnClose
            >
                <Table
                    columns={historyColumns}
                    dataSource={historyLogs}
                    rowKey="_id"
                    loading={historyLoading}
                    pagination={{
                        current: historyPagination.current,
                        pageSize: historyPagination.pageSize,
                        total: historyPagination.total,
                        onChange: (page) => fetchGlobalHistory(page)
                    }}
                />
            </Modal>
        </div>
    );
}

export default InvestorList;
